import { Fragment, ReactNode, useState } from 'react';
import { cn } from '../../../utils/className';

export interface MobileSelectionAction {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  variant?: 'default' | 'primary';
}

interface MobileSelectionBarProps {
  selectedCount: number;
  actions?: MobileSelectionAction[];
}

/**
 * MobileSelectionBar - Floating action bar at the bottom of the list it acts on.
 * It pins to the list's container, not the viewport, so a detail pane beside
 * the list stays clear. The count lives in the bar above the list.
 */
export function MobileSelectionBar({
  selectedCount,
  actions = [],
}: MobileSelectionBarProps) {
  const [shouldRender, setShouldRender] = useState(false);

  const hasSelection = selectedCount > 0;

  // Keep component mounted if opening or if we need to animate out
  const show = hasSelection || shouldRender;

  const handleTransitionEnd = () => {
    if (!hasSelection) {
      setShouldRender(false);
    }
  };

  // Show immediately when opening
  if (hasSelection && !shouldRender) {
    setShouldRender(true);
  }

  if (!show) return null;

  return (
    <div
      className={cn(
        'absolute right-4 bottom-8 left-4 z-50',
        // The app bar's gradient over white, so the list never shows through.
        `bg-surface-primary from-surface-focus/75 to-surface-focus
        rounded-[20px] bg-linear-66 shadow-lg`,
        'flex items-center justify-between px-8 py-4',
        'transition-all duration-200 ease-out',
        hasSelection
          ? 'translate-y-0 opacity-100'
          : 'translate-y-[calc(100%+2rem)] opacity-0'
      )}
      onTransitionEnd={handleTransitionEnd}
    >
      {actions.map((action, index) => (
        <Fragment key={action.label}>
          {index > 0 && (
            <span aria-hidden className='bg-inverted/60 h-6 w-px shrink-0' />
          )}
          <button
            type='button'
            onClick={action.onClick}
            className={cn(
              'flex items-center gap-2 text-white',
              'cursor-pointer transition-opacity hover:opacity-80',
              `focus-visible:rounded focus-visible:ring-1
              focus-visible:ring-white focus-visible:outline-none`
            )}
          >
            {action.icon && (
              <span
                className='flex h-4 w-4 shrink-0 items-center justify-center'
              >
                {action.icon}
              </span>
            )}
            <span className='size-p1 font-bold'>{action.label}</span>
          </button>
        </Fragment>
      ))}
    </div>
  );
}
